import type {
  ConsultationDataSection,
  ConsultationDataTable,
  ConsultationDataTone,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/Badge';

/** Cor do valor: so o que o proprio dado diz se e bom ou ruim tem cor. */
const coresDoValor: Partial<Record<ConsultationDataTone, string>> = {
  positivo: 'font-semibold text-success-700',
  negativo: 'font-semibold text-danger-700',
};

/** Linha colorida no alto do cartao, com o resumo do quadro. */
const coresDoQuadro: Partial<Record<ConsultationDataTone, string>> = {
  positivo: 'border-t-success-500',
  negativo: 'border-t-danger-500',
};

function ListaDoQuadro({ tabela }: { tabela: ConsultationDataTable }) {
  const ultima = tabela.columns.length - 1;
  const eValor = (indice: number) =>
    indice === ultima && tabela.columns[indice] === 'Valor';
  return (
    <div className="flex flex-col gap-1.5">
      <h5 className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
        {tabela.title}
      </h5>
      <div className="max-h-72 overflow-auto rounded-md border border-line">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-surface-sunken">
            <tr>
              {tabela.columns.map((coluna, indice) => (
                <th
                  key={coluna}
                  scope="col"
                  className={cn(
                    'whitespace-nowrap px-3 py-2 font-semibold text-ink-700',
                    eValor(indice) ? 'text-right' : 'text-left',
                  )}
                >
                  {coluna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabela.rows.map((linha, numero) => (
              <tr
                key={`${numero}-${linha[0] ?? ''}`}
                className="border-t border-line even:bg-surface-sunken/60"
              >
                {tabela.columns.map((coluna, indice) => (
                  <td
                    key={coluna}
                    className={cn(
                      'px-3 py-2 text-ink-900',
                      eValor(indice) && 'whitespace-nowrap text-right',
                    )}
                  >
                    {linha[indice] ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Quadro({ quadro }: { quadro: ConsultationDataSection }) {
  const listas = quadro.tables ?? [];
  const linhas = quadro.rows ?? [];
  return (
    <section
      aria-label={quadro.title}
      className={cn(
        'flex min-w-0 flex-col gap-3 rounded-lg border border-t-2 border-line bg-surface p-4',
        quadro.tone ? coresDoQuadro[quadro.tone] : undefined,
        listas.length > 0 && 'lg:col-span-2',
        quadro.key === 'cadastro' && 'lg:row-span-2',
      )}
    >
      <header className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-ink-900">{quadro.title}</h4>
        {quadro.tone === 'positivo' ? <Badge tone="success">Regular</Badge> : null}
        {quadro.tone === 'negativo' ? <Badge tone="danger">Atencao</Badge> : null}
      </header>
      {quadro.empty ? <p className="text-sm text-ink-500">{quadro.empty}</p> : null}
      {linhas.length > 0 ? (
        <dl
          className={cn(
            'grid gap-x-8',
            listas.length > 0 && linhas.length > 1 && 'lg:grid-cols-2',
          )}
        >
          {linhas.map((linha) => (
            <div
              key={linha.label}
              title={linha.hint}
              className="flex items-baseline justify-between gap-4 border-b border-line/60 py-1.5 last:border-b-0"
            >
              <dt className="shrink-0 text-xs text-ink-500">{linha.label}</dt>
              <dd
                className={cn(
                  'min-w-0 break-words text-right text-sm',
                  linha.tone ? coresDoValor[linha.tone] : 'text-ink-900',
                )}
              >
                {linha.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {listas.map((tabela) => (
        <ListaDoQuadro key={tabela.title} tabela={tabela} />
      ))}
    </section>
  );
}

/**
 * Tudo o que a consulta devolveu, organizado por assunto. Os quadros vem prontos do
 * servidor e sao os mesmos do arquivo da analise: a tela e o PDF dizem a mesma coisa
 * (ADR 0099).
 */
export function QuadrosDaConsulta({ quadros }: { quadros: ConsultationDataSection[] }) {
  if (quadros.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {quadros.map((quadro) => (
        <Quadro key={quadro.key} quadro={quadro} />
      ))}
    </div>
  );
}
